import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Newspaper, ArrowSquareOut, ArrowClockwise } from "@phosphor-icons/react";

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const SOURCE_COLOR = {
  "ForexLive": "text-emerald-300",
  "DailyFX": "text-sky-300",
  "Investing.com": "text-amber-300",
  "FXStreet": "text-fuchsia-300",
};

export default function ForexNews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/news/forex");
      setItems(data.items || []);
      setRefreshedAt(new Date());
    } catch (e) {
      setError("Couldn't load feed. Retry in a moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const int = setInterval(load, 10 * 60 * 1000); // refresh every 10min
    return () => clearInterval(int);
  }, []);

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="forex-news-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-1">
            <Newspaper size={12} /> Live
          </p>
          <h3 className="font-display text-lg font-semibold mt-1">Forex &amp; Market News</h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {refreshedAt ? `Updated ${relTime(refreshedAt.toISOString())}` : "Fetching…"} · ForexLive · DailyFX · Investing · FXStreet
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="news-refresh-btn"
          className="p-2 rounded-full border border-border hover:border-primary/50 disabled:opacity-40">
          <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-400 py-6 text-center" data-testid="news-error">{error}</p>
      )}

      {!error && loading && items.length === 0 && (
        <p className="text-sm text-muted-foreground py-6 text-center">Loading latest headlines…</p>
      )}

      {!error && items.length === 0 && !loading && (
        <p className="text-sm text-muted-foreground py-6 text-center" data-testid="news-empty">
          No headlines available right now.
        </p>
      )}

      <ul className="divide-y divide-border/60 max-h-[520px] overflow-auto hide-scroll" data-testid="news-list">
        {items.map((n, i) => (
          <li key={n.link || i} className="py-3">
            <a href={n.link} target="_blank" rel="noreferrer"
              data-testid={`news-item-${i}`}
              className="group block">
              <div className="flex items-start gap-2">
                <span className={`text-[10px] font-mono uppercase tracking-wider shrink-0 mt-1 ${SOURCE_COLOR[n.source] || "text-muted-foreground"}`}>
                  {n.source}
                </span>
                <div className="min-w-0">
                  <p className="text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">
                    {n.title}
                    <ArrowSquareOut size={11} className="inline ml-1 text-muted-foreground group-hover:text-primary" />
                  </p>
                  {n.summary && (
                    <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{n.summary}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground/70 mt-1">{relTime(n.published)}</p>
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
